<template>
    <div>
        <div class="p-grid">
            <div class="p-col-12">
                <div class="card">
                    <div class="flex justify-between items-center mb-4">
                        <div>
                            <Link :href="route('admin.index')" class="text-blue-600 hover:underline">
                                ← Kembali ke Admin Management
                            </Link>
                            <h2 class="text-2xl font-bold mt-2">Menu Management</h2>
                        </div>
                        <Button label="Tambah Menu" icon="pi pi-plus" class="p-button-primary" @click="showAddDialog" />
                    </div>
                </div>
            </div>
            
            <div class="p-col-12 md:p-col-8">
                <div class="card">
                    <h3 class="text-lg font-semibold mb-4">Struktur Menu (Drag untuk mengatur urutan)</h3>
                    <div class="border rounded-lg p-4 min-h-96">
                        <Tree 
                            :value="menuTree" 
                            :expandedKeys="expandedKeys"
                            @node-expand="onNodeExpand"
                            @node-collapse="onNodeCollapse"
                            class="w-full"
                        >
                            <template #default="slotProps">
                                <div class="flex items-center justify-between w-full py-1 group">
                                    <div class="flex items-center gap-2">
                                        <i :class="'pi ' + slotProps.node.icon" class="text-blue-500"></i>
                                        <span class="font-medium">{{ slotProps.node.label }}</span>
                                        <small v-if="slotProps.node.to" class="text-gray-400">({{ slotProps.node.to }})</small>
                                    </div>
                                    <div class="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Button icon="pi pi-plus" class="p-button-success p-button-text p-button-sm" 
                                                v-tooltip="'Tambah Sub-Menu'" @click.stop="showAddDialog(slotProps.node)" />
                                        <Button icon="pi pi-pencil" class="p-button-info p-button-text p-button-sm" 
                                                v-tooltip="'Edit'" @click.stop="editMenu(slotProps.node)" />
                                    </div>
                                </div>
                            </template>
                        </Tree>
                    </div>
                </div>
            </div>
            
            <div class="p-col-12 md:p-col-4">
                <div class="card">
                    <h3 class="text-lg font-semibold mb-4">Info Menu</h3>
                    <div v-if="selectedMenu" class="space-y-3">
                        <div>
                            <label class="text-gray-500 text-sm">Label</label>
                            <p class="font-medium">{{ selectedMenu.label }}</p>
                        </div>
                        <div>
                            <label class="text-gray-500 text-sm">Icon</label>
                            <p class="font-medium"><i :class="'pi ' + selectedMenu.icon"></i> {{ selectedMenu.icon }}</p>
                        </div>
                        <div>
                            <label class="text-gray-500 text-sm">Route (to)</label>
                            <p class="font-medium">{{ selectedMenu.to || '-' }}</p>
                        </div>
                        <div>
                            <label class="text-gray-500 text-sm">Order</label>
                            <p class="font-medium">{{ selectedMenu.order }}</p>
                        </div>
                    </div>
                    <div v-else class="text-gray-400 text-center py-8">
                        Klik menu untuk melihat detail
                    </div>
                </div>
            </div>
        </div>
        
        <!-- Add/Edit Menu Dialog -->
        <Dialog v-model:visible="menuDialog" :style="{width: '500px'}" :header="editingMenu ? 'Edit Menu' : 'Tambah Menu'" :modal="true">
            <div class="space-y-4">
                <div class="field">
                    <label for="label" class="font-semibold">Label *</label>
                    <InputText id="label" v-model="menuForm.label" class="w-full" placeholder="contoh: Dashboard" />
                </div>
                
                <div class="field">
                    <label for="icon" class="font-semibold">Icon (PrimeIcons) *</label>
                    <div class="p-inputgroup">
                        <span class="p-inputgroup-addon">
                            <i :class="'pi ' + menuForm.icon"></i>
                        </span>
                        <InputText id="icon" v-model="menuForm.icon" class="w-full" placeholder="contoh: pi-home" />
                    </div>
                </div>
                
                <div class="field">
                    <label for="to" class="font-semibold">Route (to)</label>
                    <InputText id="to" v-model="menuForm.to" class="w-full" placeholder="contoh: /dashboard" />
                </div>
                
                <div class="field">
                    <label for="url" class="font-semibold">External URL</label>
                    <InputText id="url" v-model="menuForm.url" class="w-full" placeholder="contoh: https://example.com" />
                </div>
                
                <div class="field">
                    <label for="parent" class="font-semibold">Parent Menu</label>
                    <Dropdown id="parent" v-model="menuForm.parent_id" :options="flatMenus" optionLabel="label" 
                              optionValue="id" placeholder="Pilih parent menu (kosongkan untuk root)" 
                              class="w-full" :showClear="true" />
                </div>
                
                <div class="field">
                    <label for="order" class="font-semibold">Order</label>
                    <InputNumber id="order" v-model="menuForm.order" class="w-full" placeholder="Urutan tampil" />
                </div>
                
                <div class="flex gap-4">
                    <div class="field-checkbox">
                        <Checkbox id="visible" v-model="menuForm.visible" :binary="true" />
                        <label for="visible" class="ml-2">Visible</label>
                    </div>
                    <div class="field-checkbox">
                        <Checkbox id="disabled" v-model="menuForm.disabled" :binary="true" />
                        <label for="disabled" class="ml-2">Disabled</label>
                    </div>
                    <div class="field-checkbox">
                        <Checkbox id="separator" v-model="menuForm.separator" :binary="true" />
                        <label for="separator" class="ml-2">Separator</label>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button label="Batal" icon="pi pi-times" class="p-button-text" @click="menuDialog = false" />
                <Button :label="editingMenu ? 'Update' : 'Simpan'" icon="pi pi-save" class="p-button-primary" 
                        @click="saveMenu" :loading="saving" />
            </template>
        </Dialog>
        
        <!-- Delete Confirmation Dialog -->
        <Dialog v-model:visible="deleteDialog" :style="{width: '450px'}" header="Konfirmasi Hapus" :modal="true">
            <div class="confirmation-content">
                <i class="pi pi-exclamation-triangle mr-3" style="font-size: 2rem; color: var(--red-500)" />
                <span v-if="deletingMenu">Apakah Anda yakin ingin menghapus menu <b>{{ deletingMenu.label }}</b>?</span>
            </div>
            <template #footer>
                <Button label="Batal" icon="pi pi-times" class="p-button-text" @click="deleteDialog = false" />
                <Button label="Hapus" icon="pi pi-trash" class="p-button-danger" @click="deleteMenu" :loading="deleting" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import Tree from "primevue/tree";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import Dropdown from "primevue/dropdown";
import Checkbox from "primevue/checkbox";
import Tooltip from "primevue/tooltip";
import { Link } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';

export default {
    name: "MenusIndex",
    layout: AppLayout,
    components: {
        Tree,
        Button,
        Dialog,
        InputText,
        InputNumber,
        Dropdown,
        Checkbox,
        Link,
    },
    directives: {
        tooltip: Tooltip,
    },
    props: {
        menus: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            expandedKeys: {},
            selectedMenu: null,
            menuDialog: false,
            editingMenu: null,
            menuForm: {
                label: '',
                icon: 'pi-circle',
                to: '',
                url: '',
                parent_id: null,
                order: 1,
                visible: true,
                disabled: false,
                separator: false,
            },
            saving: false,
            deleteDialog: false,
            deletingMenu: null,
            deleting: false,
        };
    },
    computed: {
        menuTree() {
            return this.transformMenusToTree(this.menus);
        },
        flatMenus() {
            const flat = [];
            const flatten = (items, level = 0) => {
                items.forEach(item => {
                    flat.push({
                        id: item.id,
                        label: '—'.repeat(level) + ' ' + item.text,
                    });
                    if (item.children) {
                        flatten(item.children, level + 1);
                    }
                });
            };
            flatten(this.menus);
            return flat;
        },
    },
    mounted() {
        this.expandAll();
    },
    methods: {
        transformMenusToTree(menus) {
            return menus.map(menu => ({
                key: menu.id.toString(),
                label: menu.text,
                icon: menu.icon || 'pi-circle',
                to: menu.to,
                order: menu.order,
                data: menu,
                children: menu.children ? this.transformMenusToTree(menu.children) : undefined,
            }));
        },
        expandAll() {
            const expand = (items) => {
                items.forEach(item => {
                    this.expandedKeys[item.id.toString()] = true;
                    if (item.children) {
                        expand(item.children);
                    }
                });
            };
            expand(this.menus);
            this.expandedKeys = { ...this.expandedKeys };
        },
        onNodeExpand(node) {
            this.expandedKeys[node.key] = true;
            this.expandedKeys = { ...this.expandedKeys };
        },
        onNodeCollapse(node) {
            delete this.expandedKeys[node.key];
            this.expandedKeys = { ...this.expandedKeys };
        },
        showAddDialog(parentNode = null) {
            this.editingMenu = null;
            this.menuForm = {
                label: '',
                icon: 'pi-circle',
                to: '',
                url: '',
                parent_id: parentNode?.data?.id || null,
                order: 1,
                visible: true,
                disabled: false,
                separator: false,
            };
            this.menuDialog = true;
        },
        editMenu(node) {
            this.editingMenu = node.data;
            this.selectedMenu = node.data;
            this.menuForm = {
                label: node.data.text,
                icon: node.data.icon || 'pi-circle',
                to: node.data.to || '',
                url: node.data.url || '',
                parent_id: node.data.parent_id,
                order: node.data.order || 1,
                visible: Boolean(node.data.visible),
                disabled: Boolean(node.data.disabled),
                separator: Boolean(node.data.separator),
            };
            this.menuDialog = true;
        },
        saveMenu() {
            this.saving = true;
            
            if (this.editingMenu) {
                Inertia.put(this.route('admin.menus.update', this.editingMenu.id), this.menuForm, {
                    onSuccess: () => {
                        this.menuDialog = false;
                        this.saving = false;
                    },
                    onError: () => {
                        this.saving = false;
                    }
                });
            } else {
                Inertia.post(this.route('admin.menus.store'), this.menuForm, {
                    onSuccess: () => {
                        this.menuDialog = false;
                        this.saving = false;
                    },
                    onError: () => {
                        this.saving = false;
                    }
                });
            }
        },
        confirmDelete(node) {
            this.deletingMenu = node.data;
            this.deleteDialog = true;
        },
        deleteMenu() {
            this.deleting = true;
            Inertia.delete(this.route('admin.menus.delete', this.deletingMenu.id), {
                onSuccess: () => {
                    this.deleteDialog = false;
                    this.deleting = false;
                },
                onError: () => {
                    this.deleting = false;
                }
            });
        },
    },
}
</script>

<style scoped>
.field {
    margin-bottom: 0.5rem;
}
.confirmation-content {
    display: flex;
    align-items: center;
}
</style>
